import { useState, useEffect, createContext, useContext, ReactNode } from 'react';

interface ABTest {
  id: string;
  name: string;
  variants: {
    [key: string]: {
      weight: number;
      config: unknown;
    };
  };
  status: 'active' | 'paused' | 'completed';
}

interface ABTestContextType {
  getVariant: (testId: string) => string | null;
  trackConversion: (testId: string, conversionType: string, value?: number) => void;
  isInTest: (testId: string) => boolean;
}

const ABTestContext = createContext<ABTestContextType | null>(null);

export const useABTest = () => {
  const context = useContext(ABTestContext);
  if (!context) {
    throw new Error('useABTest must be used within ABTestProvider');
  }
  return context;
};

export const useOptionalABTest = () => useContext(ABTestContext);

// Predefined A/B tests for revenue optimization
const AB_TESTS: ABTest[] = [
  {
    id: 'ad-density-test',
    name: 'Ad Density Optimization',
    variants: {
      'control': { weight: 50, config: { adDensity: 'normal', adsPerPage: 3 } },
      'high-density': { weight: 50, config: { adDensity: 'high', adsPerPage: 5 } }
    },
    status: 'active'
  },
  {
    id: 'affiliate-cta-test',
    name: 'Affiliate CTA Placement',
    variants: {
      'control': { weight: 50, config: { ctaPosition: 'bottom', ctaStyle: 'button' } },
      'prominent': { weight: 50, config: { ctaPosition: 'top', ctaStyle: 'banner' } }
    },
    status: 'active'
  },
  {
    id: 'rpp-default-test',
    name: 'RPP Toggle Default Setting',
    variants: {
      'control': { weight: 50, config: { rppDefault: false } },
      'rpp-enabled': { weight: 50, config: { rppDefault: true } }
    },
    status: 'active'
  },
  {
    id: 'pro-upgrade-test',
    name: 'Pro Upgrade Prompt Timing',
    variants: {
      'control': { weight: 50, config: { promptAfter: 3, promptType: 'modal' } },
      'early-prompt': { weight: 50, config: { promptAfter: 1, promptType: 'banner' } }
    },
    status: 'active'
  }
];

interface ABTestProviderProps {
  children: ReactNode;
}

export const ABTestProvider = ({ children }: ABTestProviderProps) => {
  const [userVariants, setUserVariants] = useState<{ [testId: string]: string }>({});

  useEffect(() => {
    // Load existing variant assignments from localStorage
    const savedVariants = localStorage.getItem('ab-test-variants');
    if (savedVariants) {
      try {
        setUserVariants(JSON.parse(savedVariants));
      } catch (error) {
        console.warn('Failed to parse saved A/B test variants:', error);
      }
    }

    // Assign variants for active tests that user hasn't been assigned to
    const newVariants: { [testId: string]: string } = {};
    let hasNewAssignments = false;

    AB_TESTS.forEach(test => {
      if (test.status === 'active' && !userVariants[test.id]) {
        const variant = assignUserToVariant(test);
        newVariants[test.id] = variant;
        hasNewAssignments = true;
      }
    });

    if (hasNewAssignments) {
      const updatedVariants = { ...userVariants, ...newVariants };
      setUserVariants(updatedVariants);
      localStorage.setItem('ab-test-variants', JSON.stringify(updatedVariants));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount for variant assignment
  }, []);

  const assignUserToVariant = (test: ABTest): string => {
    const variants = Object.entries(test.variants);
    const totalWeight = variants.reduce((sum, [, variant]) => sum + variant.weight, 0);
    const random = Math.random() * totalWeight;
    
    let currentWeight = 0;
    for (const [variantId, variant] of variants) {
      currentWeight += variant.weight;
      if (random <= currentWeight) {
        return variantId;
      }
    }
    
    // Fallback to first variant
    return variants[0][0];
  };

  const getVariant = (testId: string): string | null => {
    const test = AB_TESTS.find(t => t.id === testId);
    if (!test || test.status !== 'active') {
      return null;
    }
    return userVariants[testId] || null;
  };

  const trackConversion = (testId: string, conversionType: string, value?: number) => {
    const variant = getVariant(testId);
    if (!variant) return;

    // Send to analytics if available
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'ab_test_conversion', {
        test_id: testId,
        variant: variant,
        conversion_type: conversionType,
        value: value || 1,
        event_category: 'A/B Testing'
      });
    }

    // Also log to console for debugging
    console.log('A/B Test Conversion:', {
      testId,
      variant,
      conversionType,
      value
    });
  };

  const isInTest = (testId: string): boolean => {
    return getVariant(testId) !== null;
  };

  return (
    <ABTestContext.Provider value={{ getVariant, trackConversion, isInTest }}>
      {children}
    </ABTestContext.Provider>
  );
};

// Hook to get test configuration for a specific test
export const useTestConfig = (testId: string) => {
  const { getVariant } = useABTest();
  const variant = getVariant(testId);
  
  if (!variant) return null;
  
  const test = AB_TESTS.find(t => t.id === testId);
  return test?.variants[variant]?.config || null;
};

// Component to conditionally render based on A/B test variant
interface ABTestVariantProps {
  testId: string;
  variant: string;
  children: ReactNode;
}

export const ABTestVariant = ({ testId, variant, children }: ABTestVariantProps) => {
  const { getVariant } = useABTest();
  const userVariant = getVariant(testId);
  
  if (userVariant === variant) {
    return <>{children}</>;
  }
  
  return null;
};

export default ABTestProvider;